import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import GetAppIcon from '@mui/icons-material/GetApp';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import { DocumentationPlusIcon } from 'components/core/Navigation/NavigationDrawer/icons';
import { Box, IconButton, Tooltip } from '@mui/material';
import styles from '../styles';
import { useDisplayedFilesContext } from '../../../dataProviders/withDisplayedFiles';
import { useGraphicUploaderContext } from '..';
import { downloadFile } from 'services/fileOperations';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { createDeleteDocumentDialog, createUnlinkDocumentationDialog } from 'components/core/Dialog/common/dialogTemplates';
import { useEditDescriptionForm } from '../../../dataCreationForms/withEditDescriptionForm';
import { useIntl } from 'react-intl';
import {
  disabledIconButtonSx,
  iconButtonSx,
} from '../../gallery/actions/styles';
import { showGenericErrorToast } from 'services/toaster';
import {
  fileKindService,
  signedRequestService,
  canExportFileToDocumentation,
} from 'services/fileService';
import { getIssueAttachmentDisplayName } from 'services/fileService/common/getFileDisplayName';
import { useExportToDocumentationOptional } from 'components/documentation/ExportToDocumentationDialog';
import { GRAPHIC_UPLOADER_GALLERY_MODE } from '../types';
import { listImageActionsContainerSx } from '../styles';
import type { ListImageProps } from './types';
import type { DisplayedFile } from '../../gallery/filesStore';
import { VideoListItem } from './renderers/VideoListItem';
import { ImageFileListItem } from './renderers/ImageListItem';
import { GenericListItem } from './renderers/GenericListItem';
import { useHasBeenVisibleOnce } from 'hooks/useIsVisible';
import { useSingleDoubleClick } from 'hooks/useSingleDoubleClick';
import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import { IssueTranslationKeys } from 'intl/keys';
import { resolveGalleryListRoute } from '../../gallery/fileKindRoutingModel';

function needsRemoteSource(file: DisplayedFile): boolean {
  // A freshly uploaded file gets a local blob thumbnail/src immediately, so
  // "has a thumbnail" alone never signals that the real, persistent signed
  // URL from the backend has been fetched. Once the upload is confirmed
  // (has `_id`), keep prefetching until a signed request actually arrives —
  // otherwise the gallery is stuck showing (or losing, once the blob is
  // revoked) the transient local preview until the next full page load.
  return Boolean(file._id) && !file.signedRequest;
}

const REMOVE_TOOLTIP_KEY = {
  linkedDoc: IssueTranslationKeys.previewGalleryLinkedDocUnlinkLabel,
  file: IssueTranslationKeys.previewGalleryFileRemoveLabel,
} as const;

export const ListImage: React.FC<ListImageProps> = (props) => {
  const cs = styles();
  const { useDisplayedFiles, downloadFile: downloadFileFromContext } =
    useDisplayedFilesContext();
  const {
    onClick,
    id,
    drawing,
    file: fileProp,
    hideExportToDocumentation,
    hideRemove,
    hideEdit,
    selected = false,
    focused = false,
    onSelect,
  } = props;
  // An error belongs to the image it happened for: an image that gets a new id or source is tried again.
  const [imageIdentityWithError, setImageIdentityWithError] = useState<string>();
  const [imageIdentityWithVideoPreviewError, setImageIdentityWithVideoPreviewError] = useState<string>();
  const [showActions, setShowActions] = useState(false);
  const listItemRef = useRef<HTMLLIElement>(null);
  const hasBeenVisible = useHasBeenVisibleOnce(listItemRef);

  useEffect(() => {
    if (focused && listItemRef.current) {
      listItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focused]);

  const { config, removeFile, drawings } = useGraphicUploaderContext();
  const { setIndex, openDialog } = useEditDescriptionForm();
  const createDialog = useDialog();
  const intl = useIntl();
  const exportContext = useExportToDocumentationOptional();
  const openExportDialog = exportContext?.openExportDialog;

  const fileSelector = useMemo(() => {
    return function fileAtIndexSelector(
      files: DisplayedFile[]
    ): DisplayedFile | undefined {
      return files[id];
    };
  }, [id]);
  const [imageFromStore] = useDisplayedFiles(fileSelector);
  const image = fileProp ?? imageFromStore;
  const imageIdentity = image?._id || image?.src;
  const error = imageIdentityWithError !== undefined && imageIdentityWithError === imageIdentity;
  const videoPreviewError =
    imageIdentityWithVideoPreviewError !== undefined && imageIdentityWithVideoPreviewError === imageIdentity;
  const markVideoPreviewError = useCallback(
    () => setImageIdentityWithVideoPreviewError(imageIdentity),
    [imageIdentity]
  );

  const triggerPrefetchIfNeeded = useCallback(
    (file?: DisplayedFile) => {
      if (!file) {
        return;
      }

      const hasMissingOrErroredThumbnail =
        !file.thumbnail || file.thumbnail === DOCUMENT_ERROR_SOURCE;

      const shouldHydrateConvertedPdfPreview =
        fileKindService.isPdf(file) &&
        fileKindService.isConvertedPdf(file) &&
        !fileKindService.shouldShowAsImageInGallery(file);

      if (
        !shouldHydrateConvertedPdfPreview &&
        !needsRemoteSource(file) &&
        (file.loading === false || Boolean(file.signedRequest)) &&
        !hasMissingOrErroredThumbnail
      ) {
        return;
      }

      if (
        !shouldHydrateConvertedPdfPreview &&
        !needsRemoteSource(file) &&
        file.downloadSrc &&
        file.loading !== true &&
        !hasMissingOrErroredThumbnail
      ) {
        return;
      }

      downloadFileFromContext(file);
    },
    [downloadFileFromContext]
  );

  const imageRef = useRef(image);
  imageRef.current = image;

  const hasMissingOrErroredThumbnail =
    !image?.thumbnail || image?.thumbnail === DOCUMENT_ERROR_SOURCE;

  const imageNeedsRemoteSource = Boolean(image && needsRemoteSource(image));

  const shouldTriggerPrefetch =
    image &&
    (image.loading === true ||
      hasMissingOrErroredThumbnail ||
      imageNeedsRemoteSource) &&
    (!image.signedRequest ||
      !image.downloadSrc ||
      hasMissingOrErroredThumbnail ||
      imageNeedsRemoteSource);

  useEffect(() => {
    if (!hasBeenVisible || !shouldTriggerPrefetch) {
      return;
    }

    const currentImage = imageRef.current;
    if (currentImage) {
      triggerPrefetchIfNeeded(currentImage);
    }
  }, [hasBeenVisible, shouldTriggerPrefetch, triggerPrefetchIfNeeded]);

  const description = getIssueAttachmentDisplayName(image);

  const [isLoaded, setIsLoaded] = useState(false);

  const currentImageSignedRequest = image?.signedRequest?.imageSignedRequest;
  useEffect(() => {
    if (currentImageSignedRequest) {
      setIsLoaded(false);
    }
  }, [currentImageSignedRequest]);

  const setLoaded = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const onError = useCallback(() => {
    if (imageIdentity) {
      setImageIdentityWithError(imageIdentity);
    }
    setIsLoaded(true);
  }, [imageIdentity]);

  const handleDownload = useCallback(() => {
    if (!image) return;
    const src = signedRequestService.resolveGalleryDownloadSource(
      image,
      drawings[id]?.mergedImageUrl
    );
    if (src) {
      downloadFile(src, image.title, image.data?.extension).catch(() =>
        showGenericErrorToast()
      );
    }
  }, [image, drawings, id]);

  const handleEdit = useCallback(() => {
    if (config.previewOnly) return;
    setIndex(id);
    openDialog();
  }, [config.previewOnly, setIndex, id, openDialog]);

  const isLinkedDocumentationMode =
    config.galleryMode === GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_LINKED_DOCUMENTATION;
  const shouldShowFullTitle =
    config.galleryMode === GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_ATTACHMENTS ||
    isLinkedDocumentationMode;

  const handleRemove = useCallback(() => {
    const titleForDialog = getIssueAttachmentDisplayName(image);
    if (isLinkedDocumentationMode) {
      createUnlinkDocumentationDialog(createDialog, intl, titleForDialog).then(() => {
        removeFile(id);
      });
      return;
    }
    createDeleteDocumentDialog(createDialog, intl, titleForDialog).then(() => {
      removeFile(id);
    });
  }, [id, createDialog, removeFile, image, intl, isLinkedDocumentationMode]);

  const handleSingleClick = useCallback(
    (e: React.SyntheticEvent) => {
      const isShiftKey =
        'shiftKey' in e && (e as React.MouseEvent).shiftKey;
      onSelect?.(id, isShiftKey);
    },
    [onSelect, id]
  );

  const handleOpenGallery = useCallback((e: React.SyntheticEvent) => {
    if (!image) {
      return;
    }

    if (!fileKindService.hasUsablePreviewSource(image)) {
      return;
    }

    onClick?.(e);
  }, [onClick, image]);

  const handleSelectableClick = useSingleDoubleClick({
    onSingleClick: handleSingleClick,
    onDoubleClick: handleOpenGallery,
  });

  if (!image || image.deleted) {
    return null;
  }

  const noPermissionInfo = formatTranslation(intl, IssueTranslationKeys.previewNoEditPermissionInfo,);

  const removeTooltip = formatTranslation(
    intl,
    REMOVE_TOOLTIP_KEY[isLinkedDocumentationMode ? 'linkedDoc' : 'file']
  );

  const actionIcon = (
    <Box sx={listImageActionsContainerSx}>
      {!hideRemove && (
        <Tooltip
          title={
            config.previewOnly
              ? noPermissionInfo
              : removeTooltip
          }
        >
          <span>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              size='small'
              disabled={config.previewOnly}
              sx={config.previewOnly ? disabledIconButtonSx : iconButtonSx}
            >
              {isLinkedDocumentationMode ? (
                <LinkOffIcon fontSize='small' />
              ) : (
                <DeleteForeverIcon fontSize='small' />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Tooltip title={formatTranslation(intl, CommonTranslationKeys.download)}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleDownload();
          }}
          size='small'
          sx={iconButtonSx}
        >
          <GetAppIcon fontSize='small' />
        </IconButton>
      </Tooltip>

      {!hideExportToDocumentation && openExportDialog && canExportFileToDocumentation(image) && (
        <Tooltip
          title={formatTranslation(intl, CommonTranslationKeys.exportToDocumentation)}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              openExportDialog(image);
            }}
            size='small'
            sx={iconButtonSx}
          >
            <DocumentationPlusIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      )}

      {!hideEdit && (
        <Tooltip
          title={
            config.previewOnly
              ? noPermissionInfo
              : formatTranslation(intl, CommonTranslationKeys.edit)
          }
        >
          <span>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
              size='small'
              disabled={config.previewOnly}
              sx={config.previewOnly ? disabledIconButtonSx : iconButtonSx}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );

  const resolvedOnClick = onSelect ? handleSelectableClick : handleOpenGallery;
  const effectiveShowActions = showActions && !selected;

  const listRoute = resolveGalleryListRoute(image);

  if (listRoute === 'video') {
    return (
      <VideoListItem
        image={image}
        thumbnailSize={props.thumbnailSize}
        listItemRef={listItemRef}
        onClick={resolvedOnClick}
        showActions={effectiveShowActions}
        setShowActions={setShowActions}
        actionIcon={actionIcon}
        description={description}
        showFullTitle={shouldShowFullTitle}
        videoPreviewError={videoPreviewError}
        onVideoPreviewError={markVideoPreviewError}
        selected={selected}
        focused={focused}
      />
    );
  }

  if (listRoute === 'galleryImage') {
    return (
      <ImageFileListItem
        image={image}
        drawing={drawing}
        thumbnailSize={props.thumbnailSize}
        listItemRef={listItemRef}
        onClick={resolvedOnClick}
        showActions={effectiveShowActions}
        setShowActions={setShowActions}
        actionIcon={actionIcon}
        description={description}
        showFullTitle={shouldShowFullTitle}
        error={error}
        isLoaded={isLoaded}
        setLoaded={setLoaded}
        onError={onError}
        imageClassName={cs.image}
        selected={selected}
        focused={focused}
      />
    );
  }

  return (
    <GenericListItem
      image={image}
      thumbnailSize={props.thumbnailSize}
      listItemRef={listItemRef}
      onClick={resolvedOnClick}
      showActions={effectiveShowActions}
      setShowActions={setShowActions}
      actionIcon={actionIcon}
      description={description}
      showFullTitle={shouldShowFullTitle}
      selected={selected}
      focused={focused}
      lazy={!hasBeenVisible}
    />
  );
};
